// The compose page's microphone as a pure machine: what a tap, a recording
// or a finished transcription does to the page's mode, and which side
// effect the handler must run for it. The page owns the recorder, the voice
// and the network; this module only decides, so the races between them can
// be pinned without a DOM.
import type { DescribePhase } from "./describe-rung";
import type { SpeakOutcome } from "./read-aloud";
import type { TalkState } from "./talk-button";

export type Purpose =
  | { readonly kind: "dictate" }
  | { readonly kind: "re-say"; readonly spanIds: readonly string[] }
  | { readonly kind: "describe"; readonly tokenId: string };

/**
 * One recording from the tap that opened the microphone to the transcript
 * that answers it. Its purpose is fixed when it starts: a transcript is
 * only ever handed to the rung that asked for it, and only the capture
 * that put the page in a mode may take it out again.
 */
export interface Capture {
  readonly id: string;
  readonly purpose: Purpose;
}

export type Mode =
  | { readonly kind: "ready" }
  | { readonly kind: "reading-back" }
  | { readonly kind: "listening"; readonly capture: Capture }
  | { readonly kind: "thinking"; readonly capture: Capture };

export const READY: Mode = { kind: "ready" };

export type MicEvent =
  /** The sun was tapped. */
  | {
      readonly type: "talk";
      readonly storyEmpty: boolean;
      readonly captureId: string;
    }
  | {
      readonly type: "read-back-ended";
      readonly outcome: SpeakOutcome;
      readonly captureId: string;
    }
  /** A rung's microphone, or "say it instead". */
  | {
      readonly type: "listen";
      readonly purpose: Purpose;
      readonly captureId: string;
    }
  /** A rung's Done. */
  | { readonly type: "stop" }
  /** The recorder handed over what it recorded. */
  | { readonly type: "recorded" }
  /** The recorder refused the recording, or the microphone is out of reach. */
  | { readonly type: "failed" }
  | { readonly type: "transcribed"; readonly captureId: string }
  /** A word's sheet closed: the child decided the word, saved it or left it. */
  | { readonly type: "word-closed"; readonly tokenId: string };

export type MicEffect =
  | "open-mic"
  | "stop-mic"
  /** Send the recording to be written down, for the capture now thinking. */
  | "transcribe"
  /** Close the microphone, or drop the transcription in flight, and keep nothing. */
  | "discard"
  /** Read the story back, the prompt for what comes next. */
  | "read-story";

export interface MicStep {
  readonly mode: Mode;
  readonly effect: MicEffect | null;
}

export const captureOf = (mode: Mode): Capture | null =>
  mode.kind === "listening" || mode.kind === "thinking" ? mode.capture : null;

const isFor = (purpose: Purpose, tokenId: string): boolean =>
  purpose.kind === "re-say"
    ? purpose.spanIds.includes(tokenId)
    : purpose.kind === "describe" && purpose.tokenId === tokenId;

const DICTATE: Purpose = { kind: "dictate" };

const stay = (mode: Mode): MicStep => ({ mode, effect: null });

const listen = (purpose: Purpose, id: string): MicStep => ({
  mode: { kind: "listening", capture: { id, purpose } },
  effect: "open-mic",
});

export function reduceMic(mode: Mode, event: MicEvent): MicStep {
  const capture = captureOf(mode);
  switch (event.type) {
    case "talk":
      // While a word's rung has the microphone the sun waits; it never
      // stops that rung's recording as its own.
      if (capture !== null && capture.purpose.kind !== "dictate") {
        return stay(mode);
      }
      if (mode.kind === "listening") return { mode, effect: "stop-mic" };
      if (mode.kind === "thinking") return stay(mode);
      // Tapping again during the read-back skips it.
      if (mode.kind === "reading-back" || event.storyEmpty) {
        return listen(DICTATE, event.captureId);
      }
      return { mode: { kind: "reading-back" }, effect: "read-story" };
    case "read-back-ended":
      // A read-back cut short (a tapped word, "hear this sentence") is not a
      // request to be heard, so the microphone stays closed.
      if (mode.kind !== "reading-back") return stay(mode);
      return event.outcome === "done"
        ? listen(DICTATE, event.captureId)
        : stay(READY);
    case "listen":
      // One capture at a time, so a transcript can never land under a
      // purpose other than the one it was recorded for.
      return capture === null
        ? listen(event.purpose, event.captureId)
        : stay(mode);
    case "stop":
      return mode.kind === "listening"
        ? { mode, effect: "stop-mic" }
        : stay(mode);
    case "recorded":
      // A recording no one asked for is not a dictation; it goes nowhere.
      return mode.kind === "listening"
        ? {
            mode: { kind: "thinking", capture: mode.capture },
            effect: "transcribe",
          }
        : stay(mode);
    case "failed":
      return mode.kind === "listening" ? stay(READY) : stay(mode);
    case "transcribed":
      return capture?.id === event.captureId ? stay(READY) : stay(mode);
    case "word-closed":
      // A recording begun from a word's rung ends with its sheet, and what it
      // caught is never sent: it would land on a word the child has decided.
      return capture !== null && isFor(capture.purpose, event.tokenId)
        ? { mode: READY, effect: "discard" }
        : stay(mode);
  }
}

/**
 * What the page says once a re-say lands: the new words, then the question,
 * when it replaced the span; a nudge on to the next rung when the package
 * refused it, but only while the word's sheet is open, so nothing is said
 * over whatever the child has moved on to.
 */
export const reSayFollowUp = (
  replaced: boolean,
  sheetOpen: boolean,
): "read-back" | "try-another-way" | null =>
  replaced ? "read-back" : sheetOpen ? "try-another-way" : null;

export const talkStateOf = (mode: Mode, micSupported: boolean): TalkState => {
  if (!micSupported) return "asleep";
  const capture = captureOf(mode);
  if (capture !== null && capture.purpose.kind !== "dictate") return "waiting";
  switch (mode.kind) {
    case "listening":
      return "listening";
    case "thinking":
      return "thinking";
    case "reading-back":
      return "reading";
    case "ready":
      return "idle";
  }
};

/**
 * The rung a capture open for this word holds on its sheet, so the stop
 * control stays in reach until the recording ends, whatever the ladder
 * comes to offer meanwhile (alternatives arriving, say).
 */
export const heldRung = (
  mode: Mode,
  tokenId: string,
): "re-say" | "tell-me-about-it" | null => {
  const capture = captureOf(mode);
  if (capture === null || !isFor(capture.purpose, tokenId)) return null;
  return capture.purpose.kind === "re-say" ? "re-say" : "tell-me-about-it";
};

/** Where this word's re-say stands: its microphone open, being written down, or neither. */
export const reSayOf = (
  mode: Mode,
  tokenId: string,
): "listening" | "thinking" | null => {
  const capture = captureOf(mode);
  return capture?.purpose.kind === "re-say" &&
    isFor(capture.purpose, tokenId) &&
    (mode.kind === "listening" || mode.kind === "thinking")
    ? mode.kind
    : null;
};

/** The describe rung's phase, the microphone's while this word's description is being captured. */
export const describePhaseOf = (
  mode: Mode,
  tokenId: string,
  otherwise: DescribePhase,
): DescribePhase => {
  const capture = captureOf(mode);
  if (capture?.purpose.kind !== "describe" || !isFor(capture.purpose, tokenId))
    return otherwise;
  return { kind: mode.kind === "listening" ? "listening" : "thinking" };
};
